import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  getTvShowDetails,
  getTvRecommendations,
  getSeasonEpisodes,
  getTvTrailerKey,
  getTrendingTvShows,
} from '../services/tmdbService';
import { addToRecentlyViewed } from '../utils/recentlyViewed';
import DetailHeader from './Others/DetailHeader';
import '../styles/MovieDetails.css';
import '../styles/TvShowDetails.css';

const SimilarCard = ({ item, type = 'tv' }) => {
  const title = item.title || item.name;
  const year = (item.release_date || item.first_air_date || '').substring(0, 4);
  const linkTo = type === 'movie' ? `/movie/${item.id}` : `/tv/${item.id}`;

  return (
    <Link to={linkTo} className="similar-card">
      <div className="similar-poster">
        {item.poster_path ? (
          <img
            src={`https://image.tmdb.org/t/p/w300${item.poster_path}`}
            alt={title}
            loading="lazy"
          />
        ) : (
          <div className="similar-poster-fallback">{title}</div>
        )}
        {typeof item.vote_average === 'number' && item.vote_average > 0 && (
          <span className="similar-rating">
            <i className="fa-solid fa-star" aria-hidden="true"></i>
            {item.vote_average.toFixed(1)}
          </span>
        )}
      </div>
      <p className="similar-card-title">{title}</p>
      {year && <p className="similar-card-year">{year}</p>}
    </Link>
  );
};

const TvShowDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [show, setShow] = useState(null);
  const [recommendations, setRecommendations] = useState([]);
  const [trending, setTrending] = useState([]);
  const [activeSeason, setActiveSeason] = useState(null);
  const [episodes, setEpisodes] = useState([]);
  const [episodesLoading, setEpisodesLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showTrailer, setShowTrailer] = useState(false);

  const load = useCallback(async () => {
    setError(null);
    try {
      const details = await getTvShowDetails(id);
      setShow(details);
      const firstSeason = details.seasons?.[0]?.season_number ?? 1;
      setActiveSeason(firstSeason);
      const [recs, trend] = await Promise.all([
        getTvRecommendations(id).catch(() => []),
        getTrendingTvShows(1).catch(() => []),
      ]);
      setRecommendations(recs.slice(0, 12));
      setTrending(trend.filter((t) => String(t.id) !== String(id)).slice(0, 12));
    } catch {
      setError('Couldn’t load this show. Please try again.');
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  // Lazy-load episodes whenever the active season changes
  useEffect(() => {
    if (!show || activeSeason == null) return;
    let cancelled = false;
    setEpisodesLoading(true);
    getSeasonEpisodes(id, activeSeason)
      .then((eps) => {
        if (!cancelled) setEpisodes(eps);
      })
      .catch(() => {
        if (!cancelled) setEpisodes([]);
      })
      .finally(() => {
        if (!cancelled) setEpisodesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [id, activeSeason, show]);

  const handlePlay = (episodeNumber) => {
    if (!show) return;
    addToRecentlyViewed({
      id: show.id,
      title: show.name,
      poster_path: show.poster_path,
      type: 'tv',
    });
    const ep = episodeNumber || 1;
    navigate(`/player/${id}?type=tv&s=${activeSeason}&e=${ep}`);
  };

  if (error) {
    return (
      <div className="detail-page">
        <DetailHeader />
        <div className="detail-error">
          <p>{error}</p>
          <button type="button" onClick={load}>Try again</button>
          <button type="button" onClick={() => navigate('/')}>Back to home</button>
        </div>
      </div>
    );
  }

  if (!show) {
    return (
      <div className="detail-page">
        <DetailHeader />
        <div className="loading">Loading</div>
      </div>
    );
  }

  const {
    name,
    tagline,
    first_air_date,
    overview,
    backdrop_path,
    vote_average,
    episode_run_time,
    genres,
    seasons,
    credits,
    number_of_seasons,
  } = show;

  const trailerKey = getTvTrailerKey(show);
  const cast = (credits?.cast || []).slice(0, 8);
  const heroBg = backdrop_path
    ? `url(https://image.tmdb.org/t/p/original/${backdrop_path})`
    : 'none';

  return (
    <div className="detail-page">
      <DetailHeader />

      <div className="detail-hero" style={{ backgroundImage: heroBg }}>
        <div className="detail-overlay" />
      </div>

      <div className="detail-content">
        <h1 className="detail-title">{name}</h1>
        {tagline && <p className="detail-tagline">&ldquo;{tagline}&rdquo;</p>}

        <div className="detail-meta-row">
          {typeof vote_average === 'number' && vote_average > 0 && (
            <span className="rating">
              <i className="fa-solid fa-star" aria-hidden="true"></i>
              {vote_average.toFixed(1)}
            </span>
          )}
          {first_air_date && <span>{first_air_date.substring(0, 4)}</span>}
          {number_of_seasons ? (
            <span>{number_of_seasons} season{number_of_seasons === 1 ? '' : 's'}</span>
          ) : null}
          {episode_run_time && episode_run_time[0] ? (
            <span>{episode_run_time[0]} min</span>
          ) : null}
        </div>

        {genres && genres.length > 0 && (
          <div className="genres">
            {genres.slice(0, 3).map((g) => (
              <span key={g.id} className="genre-badge">{g.name}</span>
            ))}
          </div>
        )}

        <div className="detail-actions">
          <button
            type="button"
            className="detail-play-button large"
            onClick={() => handlePlay(1)}
          >
            <i className="fa-solid fa-play" aria-hidden="true"></i>
            <span>Play S{activeSeason} E1</span>
          </button>
          {trailerKey && (
            <button
              type="button"
              className="detail-trailer-button"
              onClick={() => setShowTrailer(true)}
            >
              <i className="fa-solid fa-film" aria-hidden="true"></i>
              <span>Watch Trailer</span>
            </button>
          )}
        </div>

        {overview && <p className="detail-overview">{overview}</p>}

        {seasons && seasons.length > 0 && (
          <div className="seasons-section">
            <div className="seasons-header">
              <h2>Episodes</h2>
              {seasons.length > 1 && (
                <select
                  className="season-select"
                  value={activeSeason ?? ''}
                  onChange={(e) => setActiveSeason(parseInt(e.target.value, 10))}
                  aria-label="Select season"
                >
                  {seasons.map((s) => (
                    <option key={s.season_number} value={s.season_number}>
                      Season {s.season_number}
                    </option>
                  ))}
                </select>
              )}
            </div>

            {episodesLoading ? (
              <div className="loading">Loading</div>
            ) : episodes.length === 0 ? (
              <div className="empty-state">No episodes available for this season.</div>
            ) : (
              <ul className="episode-list">
                {episodes.map((ep) => (
                  <li key={ep.id} className="episode-item">
                    <button
                      type="button"
                      className="episode-button"
                      onClick={() => handlePlay(ep.episode_number)}
                    >
                      <div className="episode-thumb">
                        {ep.still_path ? (
                          <img
                            src={`https://image.tmdb.org/t/p/w300${ep.still_path}`}
                            alt={ep.name}
                            loading="lazy"
                          />
                        ) : (
                          <div className="episode-thumb-fallback">
                            <i className="fa-solid fa-film" aria-hidden="true"></i>
                          </div>
                        )}
                        <span className="episode-play-icon" aria-hidden="true">
                          <i className="fa-solid fa-play"></i>
                        </span>
                      </div>
                      <div className="episode-meta">
                        <p className="episode-title">
                          <span className="episode-num">E{ep.episode_number}</span>
                          {ep.name}
                        </p>
                        {ep.overview && <p className="episode-overview">{ep.overview}</p>}
                        <div className="episode-sub">
                          {ep.air_date && <span>{ep.air_date}</span>}
                          {ep.runtime ? <span>{ep.runtime} min</span> : null}
                        </div>
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {cast.length > 0 && (
          <div className="cast-section">
            <h3>Cast</h3>
            <div className="cast-row">
              {cast.map((actor) => (
                <div key={actor.id} className="cast-card">
                  <div className="cast-photo">
                    {actor.profile_path ? (
                      <img
                        src={`https://image.tmdb.org/t/p/w185${actor.profile_path}`}
                        alt={actor.name}
                        loading="lazy"
                      />
                    ) : (
                      <div className="cast-photo-fallback">
                        {actor.name?.[0] || '?'}
                      </div>
                    )}
                  </div>
                  <p className="cast-name">{actor.name}</p>
                  {actor.character && (
                    <p className="cast-character">{actor.character}</p>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {recommendations.length > 0 && (
          <div className="similar-section">
            <h2 className="similar-title">More Like This</h2>
            <div className="similar-grid">
              {recommendations.map((s) => (
                <SimilarCard key={s.id} item={s} type="tv" />
              ))}
            </div>
          </div>
        )}

        {trending.length > 0 && (
          <div className="similar-section">
            <h2 className="similar-title">Trending Now</h2>
            <div className="similar-grid">
              {trending.map((t) => (
                <SimilarCard key={t.id} item={t} type="tv" />
              ))}
            </div>
          </div>
        )}
      </div>

      {showTrailer && trailerKey && (
        <div
          className="trailer-modal"
          role="dialog"
          aria-modal="true"
          onClick={() => setShowTrailer(false)}
        >
          <div className="trailer-frame" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              className="trailer-close"
              onClick={() => setShowTrailer(false)}
              aria-label="Close trailer"
            >
              <i className="fa-solid fa-xmark"></i>
            </button>
            <iframe
              title={`${name} trailer`}
              src={`https://www.youtube-nocookie.com/embed/${trailerKey}?autoplay=1&rel=0`}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default TvShowDetails;
